import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import { SEARCH_DEBOUNCE_MS } from '@/shared/hooks/useDebouncedValue';

/** A change to the page's query: the query as it stands, in; the query it becomes, out. */
export type AddressChange = (current: URLSearchParams) => URLSearchParams;

/**
 * The page's query, and a way to change it in place. The request of
 * 6 October 2026 (fifth), section 4: a table's tab, filters, search, order and
 * page live in the address, so a reload or the browser's Back shows the table
 * as it was left, and "remember filters while the user moves between cases"
 * needs nothing remembered anywhere else.
 *
 * A change REPLACES the entry rather than adding one: Back from a doctor's
 * page goes to the list of doctors, not through every filter that was tried.
 * It is worked out from the query as it stands at the moment it is made, so a
 * change made by a timer (a search that waited for the typing to pause) does
 * not put back what another control changed in the meantime. What the entry
 * remembers beside its address is kept, which is how a case open over the page
 * still knows it was opened from here.
 */
export function useAddressParams(): readonly [URLSearchParams, (change: AddressChange) => void] {
  const [params, setParams] = useSearchParams();
  const location = useLocation();

  // Read through refs, so `replace` is one function for the life of the screen.
  const latest = useRef({ setParams, state: location.state as unknown });
  latest.current = { setParams, state: location.state as unknown };

  const replace = useCallback((change: AddressChange) => {
    const { setParams: set, state } = latest.current;
    set((current) => change(current), { replace: true, state });
  }, []);

  return [params, replace] as const;
}

export interface AddressSearch {
  /** What is in the box: every keystroke, at once. */
  typed: string;
  /** A keystroke. The address follows when the typing pauses; an emptied box at once. */
  type: (next: string) => void;
  /** Empties the box without a word to the address, for a Reset that writes the address itself. */
  clear: () => void;
}

/**
 * A search box whose text is kept in the page's address. The box shows every
 * keystroke; `apply` is called with the trimmed text once the typing has
 * paused, as every search in the app waits, and at once when the box is
 * emptied. It starts from what the address already holds, so a reload opens
 * with the search still in the box.
 */
export function useAddressSearch(
  applied: string | undefined,
  apply: (q: string | undefined) => void,
): AddressSearch {
  const [typed, setTyped] = useState(applied ?? '');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef(apply);
  latest.current = apply;

  const cancel = useCallback(() => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
  }, []);

  // A search still waiting when the screen goes is not sent after it.
  useEffect(() => cancel, [cancel]);

  /*
   * The address is the truth. When it changes by something other than this box
   * (the browser's Back to an entry made before the search, a Reset), the box
   * follows it, unless a keystroke is still waiting to be applied.
   */
  useEffect(() => {
    if (timer.current !== null) return;
    const text = applied ?? '';
    // What is in the box already says it, give or take a space at an end: left as typed.
    setTyped((current) => (current.trim() === text ? current : text));
  }, [applied]);

  const type = useCallback(
    (next: string) => {
      setTyped(next);
      cancel();
      const q = next.trim();
      if (q === '') {
        latest.current(undefined);
        return;
      }
      timer.current = setTimeout(() => {
        timer.current = null;
        latest.current(q);
      }, SEARCH_DEBOUNCE_MS);
    },
    [cancel],
  );

  const clear = useCallback(() => {
    cancel();
    setTyped('');
  }, [cancel]);

  return { typed, type, clear };
}
